// src/services/ErrorReporter.js
// ─────────────────────────────────────────────────────────────────────────────
// Lightweight crash/error reporter — sends JS errors to Supabase crash_reports
// table. Uses the same Leaderboard project as leaderboard.js and identity.js.
//
// Captures: window.onerror, unhandledrejection, and manual report() calls.
// Batches reports (max 1 per 5s) to avoid flooding on error loops.
// ─────────────────────────────────────────────────────────────────────────────

const BASE = 'https://bnwakofsuhnglavdwmin.supabase.co/rest/v1';
const KEY  = 'sb_publishable_mdJ0aJ7gQhe7ju3m5kr_MA_a55EaJlv';

const HEADERS = {
  'apikey':        KEY,
  'Authorization': `Bearer ${KEY}`,
  'Content-Type':  'application/json',
  'Prefer':        'return=minimal',
};

const VERSION = '2.9';
const MIN_INTERVAL_MS = 5000; // max 1 report per 5 seconds

let _lastSentAt = 0;
let _currentScene = 'unknown';

// ── Helpers ─────────────────────────────────────────────────────────────────

function _getPlayerId() {
  return localStorage.getItem('pazurumon_player_id') || 'anonymous';
}

function _getDevice() {
  const ua = navigator.userAgent || '';
  const w  = screen.width || 0;
  const h  = screen.height || 0;
  return `${w}x${h} | ${ua.slice(0, 200)}`;
}

// ── Send to Supabase ────────────────────────────────────────────────────────

async function _send(error, stack) {
  const now = Date.now();
  if (now - _lastSentAt < MIN_INTERVAL_MS) return; // throttle
  _lastSentAt = now;

  const payload = {
    player_id: _getPlayerId(),
    error:     String(error).slice(0, 500),
    stack:     String(stack || '').slice(0, 2000),
    scene:     _currentScene,
    device:    _getDevice(),
    version:   VERSION,
  };

  try {
    await fetch(`${BASE}/crash_reports`, {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify(payload),
    });
  } catch (_) {
    // silently fail — we can't report an error about error reporting
  }
}

// ── Public API ──────────────────────────────────────────────────────────────

/** Update the current scene name (call from each scene's create()) */
export function setScene(sceneName) {
  _currentScene = sceneName;
}

/** Manually report a caught error */
export function report(error, context) {
  const msg   = error?.message || String(error);
  const stack = error?.stack || context || '';
  _send(msg, stack);
}

// ── Global listeners ────────────────────────────────────────────────────────

export function init() {
  window.onerror = (message, source, lineno, colno, error) => {
    _send(
      message,
      error?.stack || `${source}:${lineno}:${colno}`,
    );
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    _send(
      reason?.message || String(reason),
      reason?.stack || '',
    );
  });
}
